/**
 * chat-director/questBeat — turn a quest moment (offer / progress / turn-in) into a
 * ground-truth Beat the director can thread + the voice layer can phrase, WITHOUT
 * letting the voice contradict the authored quest canon (D17). Mirrors society/'s
 * {@link import('@/society').showOffBeat}/{@link import('@/society').castBeat}: the
 * beat carries structured facts (questId, title, the active stage's objective, the
 * reward, and the authored dialogue line) and the voice only renders them warmly.
 *
 * Pure + deterministic: a total function of (def, stageIndex, phase, giverId, at).
 * Warm by construction — nothing here can pressure the player: quests never fail,
 * never expire, and every rendered line lands as "no rush, whenever you like."
 *
 * Agent B's GameSession.recordQuestEvent calls this to seed a chat line when a quest
 * is offered, ticks forward, or is turned in.
 */
import type {
  Beat,
  ChatChannel,
  BeatKind,
  BeatPriority,
  QuestDef,
  QuestStage,
  ObjectiveDef,
  QuestReward,
} from '@/shared';

/** The quest lifecycle moment a beat marks. */
export type QuestPhase = 'offer' | 'progress' | 'complete';

const PHASE_KIND: Readonly<Record<QuestPhase, BeatKind>> = {
  offer: 'quest-offer',
  progress: 'quest-progress',
  complete: 'quest-complete',
};

const PHASE_PRIORITY: Readonly<Record<QuestPhase, BeatPriority>> = {
  offer: 'normal',
  progress: 'normal',
  complete: 'highlight', // a turn-in is a proud, un-skippable little moment.
};

/**
 * Build the ground-truth beat for one quest moment. `stageIndex` selects the stage
 * whose authored dialogue + objective the beat carries (clamped into range so a stray
 * index never throws — beats are ephemeral). `giverId` is the offering/turn-in NPC and
 * becomes the beat's subject; the player is always involved. `at` stamps the beat for
 * ordering/freshness (caller-supplied so it stays deterministic/testable).
 */
export function questBeat(
  def: QuestDef,
  stageIndex: number,
  phase: QuestPhase,
  giverId: string,
  at = 0,
  channel: ChatChannel = 'zone',
): Beat {
  const stages = def.stages;
  const idx = stages.length === 0 ? 0 : Math.min(Math.max(0, stageIndex), stages.length - 1);
  const stage: QuestStage | undefined = stages[idx];

  const objective = stage ? summarizeStage(stage) : 'this next little step';
  const reward = summarizeReward(def.reward);
  const line = stage ? authoredLine(stage, phase) : '';

  const data: Record<string, string | number | boolean> = {
    questId: def.id,
    questTitle: def.title,
    questKind: def.kind,
    synopsis: def.synopsis,
    giverId,
    phase,
    stageIndex: idx,
    objective,
    reward,
    warm: true,
  };
  // Only carry an authored dialogue line when one exists for this phase — never
  // invent canon (progress stages have no authored line of their own).
  if (line.length > 0) data.line = line;

  return {
    id: `quest-${def.id}-${phase}-${idx}-${at}`,
    kind: PHASE_KIND[phase],
    priority: PHASE_PRIORITY[phase],
    subjectTrainerId: giverId,
    involvedTrainerIds: ['player'],
    channel,
    data,
    createdAt: at,
  };
}

/** The authored dialogue line to phrase around, per phase (empty when none). */
function authoredLine(stage: QuestStage, phase: QuestPhase): string {
  const lines = phase === 'complete' ? stage.dialogue.onComplete : stage.dialogue.onStart;
  // 'progress' has no authored line — the voice cheers the objective instead.
  if (phase === 'progress') return '';
  return (lines[0] ?? '').trim();
}

/** All of a stage's objectives, summarized warmly and joined (implicit AND). */
function summarizeStage(stage: QuestStage): string {
  const parts = stage.objectives.map(summarizeObjective).filter((s) => s.length > 0);
  if (parts.length === 0) return 'this next little step';
  if (parts.length === 1) return parts[0]!;
  return parts.slice(0, -1).join(', ') + ' and ' + parts[parts.length - 1]!;
}

/** A warm, non-pressuring phrase for a single objective. */
function summarizeObjective(o: ObjectiveDef): string {
  switch (o.kind) {
    case 'catch-species':
      return `catch a ${title(o.speciesId)}`;
    case 'catch-type':
      return `catch ${o.count} ${o.monType}-type ${plural(o.count, 'Pokémon', 'Pokémon')}`;
    case 'catch-count':
      return `catch ${o.count} wild ${plural(o.count, 'Pokémon', 'Pokémon')}`;
    case 'win-friendly-battles':
      return `enjoy ${o.count} friendly ${plural(o.count, 'battle', 'battles')}`;
    case 'defeat-gym-leader':
      return `have a friendly gym match at the ${title(o.gymId)}`;
    case 'reach-level':
      return o.speciesId
        ? `raise your ${title(o.speciesId)} to Lv. ${o.level}`
        : `raise a teammate to Lv. ${o.level}`;
    case 'visit-biome':
      return `wander over to the ${title(o.biomeId)}`;
    case 'evolve':
      return o.toSpeciesId
        ? `see one grow into a ${title(o.toSpeciesId)}`
        : `celebrate ${o.count} ${plural(o.count, 'evolution', 'evolutions')}`;
    case 'trade':
      return o.withTrainerId
        ? `share a trade with a friend`
        : `share ${o.count} ${plural(o.count, 'trade', 'trades')}`;
    case 'show-off':
      return o.count > 1 ? `show off a Pokémon ${o.count} times` : `show off a Pokémon`;
    case 'cast-move':
      return `flourish a move in the tavern`;
    case 'talk-to':
      return `say hello to a friend`;
    default:
      return 'this next little step';
  }
}

/** A warm phrase naming the reward, for the turn-in celebration. */
function summarizeReward(r: QuestReward): string {
  switch (r.kind) {
    case 'gift-mon':
      return `a ${title(r.speciesId)} to walk beside you`;
    case 'codex-unlock':
      return `a new page in your codex`;
    case 'badge':
      return `the ${title(r.badgeId)}`;
    case 'rare-candies':
      return `${r.count} Rare ${plural(r.count, 'Candy', 'Candies')}`;
    case 'evolution-stone':
      return `a ${title(r.itemId)}`;
    case 'town-prop':
      return `a little something to brighten the town`;
    case 'follower-cosmetic':
      return `a keepsake for the friend at your heel`;
    default:
      return `a warm thank-you`;
  }
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

function title(s: string): string {
  return s.replace(
    /(^|[-_\s])([a-z])/g,
    (_m, sep: string, ch: string) => (sep === '-' || sep === '_' ? ' ' : sep) + ch.toUpperCase(),
  );
}
